export interface RgbaImage {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/** VEX renders the same floor ellipse on every `vdpHeliosTablet`, so the horizon is computed. */
const TABLET_CYCLORAMA = { radiusX: 1.5, radiusY: 0.351, centreY: 1.03 };

/** Columns at each edge averaged for the colours carried outward. */
const EDGE_COLOR_COLUMNS = 5;
/** Rows this close to the horizon are anti-aliased in the source, so neither wall nor floor. */
const HORIZON_MARGIN = 3;
/** Wall measures 236 on every composite and the floor 126-155, so these bands separate them. */
const WALL_MIN = 215;
const FLOOR_RANGE = { min: 105, max: 195 };
const MAX_MISMATCHED_ROWS = 0.05;

export const studioHorizonAt = (x: number, width: number, height: number) => {
  const t = (x - width / 2) / (TABLET_CYCLORAMA.radiusX * width);

  if (Math.abs(t) >= 1) return height;

  return (
    TABLET_CYCLORAMA.centreY * height - TABLET_CYCLORAMA.radiusY * width * Math.sqrt(1 - t * t)
  );
};

/** Per-row colour of the outermost columns on one side: what the backdrop looks like there. */
const edgeColumnColors = (image: RgbaImage, side: 'left' | 'right') => {
  const { data, width, height } = image;
  const first = side === 'left' ? 0 : Math.max(0, width - EDGE_COLOR_COLUMNS);
  const last = Math.min(width, first + EDGE_COLOR_COLUMNS);
  const colors = new Float32Array(height * 3);

  for (let y = 0; y < height; y++) {
    for (let x = first; x < last; x++) {
      const i = (y * width + x) * 4;
      colors[y * 3] += data[i];
      colors[y * 3 + 1] += data[i + 1];
      colors[y * 3 + 2] += data[i + 2];
    }
    for (let k = 0; k < 3; k++) colors[y * 3 + k] /= last - first;
  }

  return colors;
};

/**
 * Whether an edge column reads as wall above the cyclorama's horizon and floor below it. A car
 * VEX smeared out to the frame edge fails this, as would a change to the backdrop's geometry.
 */
const isCyclorama = (colors: Float32Array, horizon: number, height: number) => {
  let mismatched = 0;

  for (let y = 0; y < height; y++) {
    if (Math.abs(y - horizon) <= HORIZON_MARGIN) continue;

    const grey = (colors[y * 3] + colors[y * 3 + 1] + colors[y * 3 + 2]) / 3;
    const isExpected =
      y < horizon ? grey >= WALL_MIN : grey >= FLOOR_RANGE.min && grey <= FLOOR_RANGE.max;

    if (!isExpected) mismatched += 1;
  }

  return mismatched <= height * MAX_MISMATCHED_ROWS;
};

/**
 * Widens a VEX studio composite to `aspect` by continuing its cyclorama past both edges: the
 * curved horizon carries on outward, and wall and floor take the colours of the image's own
 * outermost columns, which vary per car with its shadow. `null` when an edge is not clean
 * cyclorama — stretching it would only stretch the defect.
 */
export const extendStudioBackdrop = (image: RgbaImage, aspect: number): RgbaImage | null => {
  const { width, height, data } = image;
  const outWidth = Math.max(width, Math.round(height * aspect));
  const offset = Math.floor((outWidth - width) / 2);

  const sides = [
    { from: 0, to: offset, edgeX: 0, colors: edgeColumnColors(image, 'left') },
    {
      from: offset + width,
      to: outWidth,
      edgeX: width - 1,
      colors: edgeColumnColors(image, 'right')
    }
  ];

  const isSound = sides.every(({ edgeX, colors }) =>
    isCyclorama(colors, studioHorizonAt(edgeX, width, height), height)
  );

  if (!isSound) return null;

  const out = new Uint8ClampedArray(outWidth * height * 4);

  for (const { from, to, edgeX, colors } of sides) {
    const edgeHorizon = studioHorizonAt(edgeX, width, height);
    const wallRow = Math.max(0, Math.floor(edgeHorizon) - 4);
    const floorRow = Math.min(Math.ceil(edgeHorizon) + HORIZON_MARGIN, height - 1);

    for (let x = from; x < to; x++) {
      const horizon = studioHorizonAt(x - offset, width, height);

      for (let y = 0; y < height; y++) {
        const o = (y * outWidth + x) * 4;
        const wall = y < edgeHorizon - 2 ? y : wallRow;
        const floor = Math.max(y, floorRow);
        const mix = Math.min(Math.max(y - horizon + 0.5, 0), 1);

        for (let k = 0; k < 3; k++) {
          out[o + k] = colors[wall * 3 + k] * (1 - mix) + colors[floor * 3 + k] * mix;
        }
        out[o + 3] = 255;
      }
    }
  }

  for (let y = 0; y < height; y++) {
    out.set(data.subarray(y * width * 4, (y + 1) * width * 4), (y * outWidth + offset) * 4);
  }

  return { data: out, width: outWidth, height };
};
